import React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

const STEPS = [
  { number: 1, label: "Ad Profiles" },
  { number: 2, label: "Tracking" },
  { number: 3, label: "Naming Convention" },
  { number: 4, label: "Launch Settings" },
  { number: 5, label: "Creative Enhancements" },
];

interface OnboardingStepperProps {
  currentStep: number;
  completedSteps: Set<number>;
  onStepClick: (step: number) => void;
}

export function OnboardingStepper({
  currentStep,
  completedSteps,
  onStepClick,
}: OnboardingStepperProps) {
  return (
    <>
      {/* Desktop vertical stepper */}
      <nav className="hidden md:flex flex-col gap-1 w-[250px] shrink-0">
        {STEPS.map((step) => {
          const isActive = step.number === currentStep;
          const isCompleted = completedSteps.has(step.number);
          const isClickable = isCompleted;

          return (
            <button
              key={step.number}
              onClick={() => isClickable && onStepClick(step.number)}
              disabled={!isClickable}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors",
                isActive && "bg-primary/10",
                isClickable && !isActive && "hover:bg-muted cursor-pointer",
                !isClickable && !isActive && "cursor-default"
              )}
            >
              {/* Step indicator */}
              <div
                className={cn(
                  "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium shrink-0 transition-colors",
                  isActive && "bg-primary text-primary-foreground ring-2 ring-primary/30",
                  isCompleted && !isActive && "bg-success text-success-foreground",
                  !isActive && !isCompleted && "bg-muted text-muted-foreground"
                )}
              >
                {isCompleted && !isActive ? (
                  <Check className="h-4 w-4" />
                ) : (
                  step.number
                )}
              </div>

              {/* Step label */}
              <span
                className={cn(
                  "text-sm",
                  isActive && "font-semibold text-foreground",
                  isCompleted && !isActive && "text-muted-foreground",
                  !isActive && !isCompleted && "text-muted-foreground"
                )}
              >
                {step.label}
              </span>
            </button>
          );
        })}
      </nav>

      {/* Mobile horizontal progress bar */}
      <div className="md:hidden flex items-center gap-2 mb-4">
        <span className="text-sm font-medium text-foreground">
          Step {currentStep} of {STEPS.length}
        </span>
        <div className="flex-1 flex gap-1">
          {STEPS.map((step) => (
            <div
              key={step.number}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-colors",
                step.number <= currentStep ? "bg-primary" : "bg-muted"
              )}
            />
          ))}
        </div>
      </div>
    </>
  );
}
